import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import * as ZonesSubscriptionsActions from '../actions/zones-subscriptions.actions';
import * as ZonesSubscriptionsApiActions from '../actions/zones-subscriptions-api.actions';
import { ZoneSubscriptionsService } from '../../../zones/services/zone-subscriptions.service';
import { ZoneSubscription } from '../../../zones/models/zone-subscription';
import { ShapeDataFeature } from '../../../zones/models/shape-data-feature';
import { ShapeDataFeatureCollection } from '../../../zones/models/shape-data-feature-collection';
import { ShapeHelper } from '../../../shared/helpers/shape.helper';
import { LoadingBarState } from '@ngx-loading-bar/core/loading-bar.state';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { environment } from '../../../../environments/environment';

@Injectable()
export class ZonesSubscriptionsEffects {

  getZonesSubscriptions$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ZonesSubscriptionsActions.getZonesSubscriptions.type),
      switchMap((action: any) => this._zoneSubscriptionsService.getZones()
        .pipe(
          map((data) => {
            const normalized = data?.map(zone => this._normalizeZone(zone)) ?? data;
            return ZonesSubscriptionsApiActions.getZonesSubscriptionsSuccess({data: normalized});
          }),
          catchError(error => of(ZonesSubscriptionsApiActions.getZonesSubscriptionsFailure({error})))
        )
      )
    )
  );

  createZonesSubscription$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ZonesSubscriptionsActions.createZoneSubscription.type),
      tap(() => this._loader.start()),
      switchMap((action: any) => this._zoneSubscriptionsService.createZone(action.data)
        .pipe(
          map(zone => ZonesSubscriptionsApiActions.createZoneSubscriptionSuccess({data: this._normalizeZone(zone)})),
          catchError(error => of(ZonesSubscriptionsApiActions.createZoneSubscriptionFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  editZonesSubscription$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ZonesSubscriptionsActions.editZoneSubscription.type),
      tap(() => this._loader.start()),
      switchMap((action: any) => this._zoneSubscriptionsService.editZone(action.data)
        .pipe(
          map(zone => ZonesSubscriptionsApiActions.editZoneSubscriptionSuccess({data: this._normalizeZone(zone)})),
          catchError(error => of(ZonesSubscriptionsApiActions.editZoneSubscriptionFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  editUnitsZoneSubscription$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ZonesSubscriptionsActions.editUnitsZoneSubscription.type),
      tap(() => this._loader.start()),
      switchMap((action: any) => this._zoneSubscriptionsService.editUnitsZone(action.data)
        .pipe(
          map(zone => ZonesSubscriptionsApiActions.editUnitsZoneSubscriptionSuccess({data: zone})),
          catchError(error => of(ZonesSubscriptionsApiActions.editUnitsZoneSubscriptionFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  deleteZonesSubscription$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ZonesSubscriptionsActions.deleteZoneSubscription.type),
      tap(() => this._loader.start()),
      switchMap((action: any) => this._zoneSubscriptionsService.deleteZone(action.zoneId)
        .pipe(
          map(res => ZonesSubscriptionsApiActions.deleteZoneSubscriptionSuccess({zoneId: res})),
          catchError(error => of(ZonesSubscriptionsApiActions.deleteZoneSubscriptionFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  changeState$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ZonesSubscriptionsActions.changeState.type),
      tap(() => this._loader.start()),
      switchMap((action: any) => this._zoneSubscriptionsService.changeState(action.data)
        .pipe(
          map(res => ZonesSubscriptionsApiActions.changeStateSuccess({data: res})),
          catchError(error => of(ZonesSubscriptionsApiActions.changeStateFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  persistZones$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ZonesSubscriptionsApiActions.getZonesSubscriptionsSuccess),
      tap((action: any) => {
        try {
          localStorage.setItem(`${environment.storagePrefix}.cachedZonesSubscriptions`, JSON.stringify(action.data));
        } catch { }
      })
    ), { dispatch: false }
  );

  private _loader: LoadingBarState;

  constructor(
    private _actions$: Actions,
    private _zoneSubscriptionsService: ZoneSubscriptionsService,
    private _loadingBar: LoadingBarService
  ) {
    this._loader = this._loadingBar.useRef('main');
  }

  private _normalizeZone(zone: ZoneSubscription): ZoneSubscription {
    const shapeData: ShapeDataFeatureCollection = { type: 'FeatureCollection', features: [] };
    if (zone.shapeData['type'] === 'Feature') {
      const feature = ShapeHelper.createPolygonFromCircle(<ShapeDataFeature>zone.shapeData, zone.zoneSubscriptionId, (<ShapeDataFeature>zone.shapeData)?.properties?.shapeId);
      if (feature) {
        shapeData.features.push(feature);
        return {...zone, shapeData};
      }
    } else if (zone.shapeData['type'] === 'FeatureCollection') {
      (<ShapeDataFeatureCollection>zone.shapeData).features.forEach((x: ShapeDataFeature) => {
        const feature = ShapeHelper.createPolygonFromCircle(x, zone.zoneSubscriptionId, x.properties.shapeId);
        if (feature) {
          shapeData.features.push(feature);
        }
      });
      return {...zone, shapeData};
    }
    return zone;
  }
}
